/**
 * PaywallHost (web) — single, app-root mount of the canonical
 * PaywallSheet. Twin of src/app/components/PaywallHost.tsx.
 *
 * Subscribes to the global paywall bus (`lib/paywall.ts`) and pops
 * the sheet whenever something requests it: a screen calling
 * `requestPaywall()` explicitly, OR the convex-client interceptor in
 * `convexDispatch.ts` auto-firing on a premium-shaped ConvexError.
 *
 * Unlike mobile there's no `onPurchased` refresh hook here —
 * entitlement flips are picked up by the reactive `me` query.
 */
import { useEffect, useState } from 'react';
import { useConvexAuth } from 'convex/react';

import { PaywallSheet, type PaywallTrigger } from './PaywallSheet';
import { subscribeToPaywall, type PaywallOneTime } from '@/lib/paywall';

export function PaywallHost() {
  const [open, setOpen] = useState(false);
  // Trigger updates on every request, even if the sheet is already
  // open — keeps copy in sync with the most recent action.
  const [trigger, setTrigger] = useState<PaywallTrigger>('subscribe');
  const [oneTime, setOneTime] = useState<PaywallOneTime | undefined>(undefined);
  const { isAuthenticated } = useConvexAuth();

  useEffect(() => {
    return subscribeToPaywall((event) => {
      // Skip for signed-out callers — premium-gated calls 401 before
      // they 402, and the sheet's store-handoff CTA is meaningless
      // without an account to upgrade. Authed-but-not-loaded is fine:
      // useConvexAuth flips true once the session is hydrated.
      if (!isAuthenticated) return;
      setTrigger(event.trigger);
      setOneTime(event.oneTime);
      setOpen(true);
    });
  }, [isAuthenticated]);

  return (
    <PaywallSheet
      open={open}
      trigger={trigger}
      oneTime={oneTime}
      onClose={() => setOpen(false)}
    />
  );
}
